'use client';

import { cn } from '@/lib/utils';
import type {
  WorldCupMatchHeaderData,
  WorldCupMatchHeaderTeam,
} from '@/components/predic/types/market';
import { useTranslation } from '@/lib/i18n';
import { translateWorldCupTeamName } from './teamTranslation';

export interface WorldCupMatchHeaderProps extends WorldCupMatchHeaderData {
  className?: string;
}

export default function WorldCupMatchHeader({
  home_team,
  away_team,
  score,
  match_date,
  className,
}: WorldCupMatchHeaderProps) {
  return (
    <section
      className={cn(
        'flex w-full items-center justify-center gap-8 py-6 text-white sm:gap-16 lg:gap-25',
        className,
      )}
    >
      <TeamBlock team={home_team} />
      <div className="flex min-w-17.5 flex-col items-center justify-center gap-3">
        <div className="whitespace-nowrap text-3xl font-medium leading-10">{score}</div>
        <div className="whitespace-nowrap text-base leading-5 text-[#757575]">{match_date}</div>
      </div>
      <TeamBlock team={away_team} />
    </section>
  );
}

function TeamBlock({ team }: { team: WorldCupMatchHeaderTeam }) {
  const { t } = useTranslation();

  return (
    <div className="flex w-37.5 flex-col items-center justify-center gap-4">
      <TeamLogo team={team} />
      <div className="max-w-37.5 truncate text-center text-2xl font-medium leading-5 text-white">
        {translateWorldCupTeamName(team.name, t)}
      </div>
    </div>
  );
}

function TeamLogo({ team }: { team: WorldCupMatchHeaderTeam }) {
  if (team.logo_src) {
    return (
      <img
        src={team.logo_src}
        alt=""
        className="size-12 rounded-lg object-contain"
        loading="lazy"
      />
    );
  }

  return (
    <span className="grid size-12 place-items-center rounded-lg bg-[#24272D] text-sm font-semibold text-white">
      {team.avatar_fallback ?? team.name.slice(0, 2).toUpperCase()}
    </span>
  );
}
